/**
 * ComponentCentered — Centered family renderer.
 *
 * Variants:
 *   - "centered": images on top in a responsive grid, then titles,
 *     text, and actions stacked below, all centered.
 *   - "centered_text_only": titles and text first (prominent), images
 *     pushed to the bottom. Ideal for text-heavy/article sections.
 *   - "centered_full": images fill edge-to-edge in a responsive grid
 *     (fixed height, object-cover zoom), content stacked centered below.
 *   - "centered_carousel": images auto-rotate in a crossfade slideshow
 *     above centered content (one image visible at a time).
 *
 * Edit mode: add buttons appear after each bucket section, and empty
 * buckets show a placeholder affordance so editors can populate them.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  bucketElements,
  BucketRow,
  RenderItem,
  BucketAddButton,
  EmptyBucketPlaceholder,
  elementHasRenderableMedia,
} from "./componentModeUtils";
import { EditContext } from "../../../editor/context/EditContext";

function isFullBleed(modeId) {
  return modeId === "centered_full";
}

const FULL_BLEED_ROW_HEIGHT = 300;
// `!items-center`/`!justify-center` neutralise the per-image `position` flex
// alignment from `Image.js` so cover-cropped media stays centered in its tile
// instead of being parked in its `position` corner (e.g. bottom-right).
// `[&_.image-size-box]:!p-0` zeroes the image's OWN padding inside a cover tile —
// the tile is the layout-owned frame, so per-image padding would inset the cover
// <img> inside an `overflow-hidden` wrapper and crop its rounded corners square.
const COVER_FILL = "[&_div]:!h-full [&_div]:!w-full [&_div]:!max-w-none [&_div]:!items-center [&_div]:!justify-center [&_.image-size-box]:!p-0 [&_.progressive-image-wrapper]:!h-full [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-full [&_img]:!object-cover [&_img]:!object-center [&_img]:!max-h-none [&_img]:!max-w-none [&_img]:!box-border [&_video]:!w-full [&_video]:!h-full [&_video]:!object-cover [&_video]:!object-center";
const FILL_WIDTH = "[&_div]:!w-full [&_div]:!max-w-none [&_.progressive-image-wrapper]:!w-full [&_.progressive-image-wrapper]:!max-w-none [&_img]:!w-full [&_img]:!h-auto [&_img]:!max-w-none [&_img]:!max-h-none [&_video]:!w-full [&_video]:!h-auto [&_video]:!max-w-none";
const CONTAINED_TILE = `rounded-lg ${FILL_WIDTH}`;

function isCarousel(modeId) {
  return modeId === "centered_carousel";
}

// ---------------------------------------------------------------------------
// Carousel hook
// ---------------------------------------------------------------------------

// Slow, gentle pacing: each slide rests for ~INTERVAL, then cross-fades over
// FADE. The fade is a TRUE crossfade — the outgoing layer fades 1→0 while the
// incoming fades 0→1 at the same time (see renderCarousel), so there is no
// fade-to-blank flash between slides.
const CAROUSEL_INTERVAL_MS = 6500;
const CAROUSEL_FADE_MS = 1200;

function useCarousel(count, { paused = false } = {}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false;
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [count]);

  useEffect(() => {
    if (paused || prefersReducedMotion || count <= 1) return;

    let cancelled = false;

    // Advance directly — all slides stay mounted and only their opacity
    // changes, so the browser crossfades the two layers smoothly.
    const tick = () => {
      if (cancelled) return;
      setActiveIndex((prev) => (prev + 1) % count);
    };

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, CAROUSEL_INTERVAL_MS);
    };
    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [count, paused, prefersReducedMotion]);

  return { activeIndex };
}

// ---------------------------------------------------------------------------
// Renderer
// ---------------------------------------------------------------------------

const ComponentCentered = ({ component, elements, modeDefinition, breakpoint }) => {
  const modeId = component?.component_mode;
  const isTextOnly = modeId === "centered_text_only";
  const fullBleed = isFullBleed(modeId);
  const carousel = isCarousel(modeId);
  const { bucketed } = bucketElements(elements, modeDefinition);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  const images = bucketed.image || [];
  const titles = bucketed.title || [];
  const texts = bucketed.text || [];
  const buttonCollections = bucketed.button_collection || [];
  const buttons = bucketed.button || [];
  const files = bucketed.file || [];

  const compId = component?.id;

  const isMobile = breakpoint === "extra_small" || breakpoint === "small";
  const imageColumns = isMobile ? 1 : breakpoint === "medium" ? 2 : 3;

  const effectiveImageCols = isMobile ? 1 : Math.min(imageColumns, images.length);
  const hasSpareSlots = !isMobile && images.length > 0 && images.length < imageColumns;
  const imageGridMaxPct = hasSpareSlots
    ? Math.min(100, Math.round((images.length / imageColumns) * 130))
    : 100;

  const totalElements = elements?.length || 0;

  const { activeIndex } = useCarousel(images.length, {
    paused: !carousel,
  });

  const imagesHaveMedia = images.length > 0 && images.some(elementHasRenderableMedia);
  // The full-bleed cover path forces every BucketRow cell into a fixed-height,
  // overflow-hidden cover cell — which clips "No Image Selected" placeholders.
  // Only go full-bleed when every image has media; otherwise fall back to the
  // contained layout (which never clips placeholders).
  const imagesAllHaveMedia = images.length > 0 && images.every(elementHasRenderableMedia);

  const renderCarousel = () => {
    if (images.length === 0) return null;

    if (!imagesHaveMedia) {
      return (
        <div className="w-full">
          <RenderItem element={images[activeIndex]} />
        </div>
      );
    }

    return (
      <div className="w-full relative overflow-hidden">
        <div className="invisible" aria-hidden="true">
          <RenderItem element={images[0]} />
        </div>
        {images.map((img, idx) => (
          <div
            key={img.id}
            className="absolute inset-0 transition-opacity ease-in-out"
            style={{
              transitionDuration: `${CAROUSEL_FADE_MS}ms`,
              opacity: idx === activeIndex ? 1 : 0,
              pointerEvents: idx === activeIndex ? "auto" : "none",
            }}
          >
            <RenderItem element={img} />
          </div>
        ))}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {images.map((img, idx) => (
              <span
                key={img.id}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === activeIndex
                    ? "bg-white shadow-md scale-110"
                    : "bg-white/50"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const ImagesBlock = images.length > 0 ? (
    <>
      {carousel ? (
        renderCarousel()
      ) : fullBleed && imagesAllHaveMedia ? (
        <BucketRow
          elements={images}
          compId={compId}
          className="w-full flex flex-wrap justify-center"
          style={{ gap: 0 }}
          cellClassName={`overflow-hidden flex-shrink-0 ${COVER_FILL}`}
          cellStyle={{ width: `${100 / effectiveImageCols}%`, height: `${FULL_BLEED_ROW_HEIGHT}px` }}
        />
      ) : (
        <BucketRow
          elements={images}
          compId={compId}
          className={`flex flex-wrap justify-center ${hasSpareSlots ? "mx-auto" : "w-full"}`}
          style={{
            gap: "8px",
            ...(hasSpareSlots ? { width: "100%", maxWidth: `${imageGridMaxPct}%` } : {}),
          }}
          cellClassName={CONTAINED_TILE}
          cellStyle={{ width: `calc(${100 / effectiveImageCols}% - ${((effectiveImageCols - 1) * 8) / effectiveImageCols}px)` }}
        />
      )}
      {isEditMode && (
        <BucketAddButton compId={compId} bucket="image" order={totalElements} className="-mt-4 z-10" />
      )}
    </>
  ) : isEditMode ? (
    <EmptyBucketPlaceholder compId={compId} bucket="image" order={totalElements} />
  ) : null;

  const contentItems = [...titles, ...texts, ...buttonCollections, ...buttons]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const ContentBlock = (
    <div className="w-full flex flex-col items-center">
      {contentItems.length > 0 && (
        <BucketRow elements={contentItems} compId={compId} className="w-full" />
      )}

      {isEditMode && (
        <>
          {titles.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="title" order={totalElements} />
          )}
          {texts.length === 0 && (
            <EmptyBucketPlaceholder compId={compId} bucket="text" order={totalElements} />
          )}
          {contentItems.length > 0 && (
            <BucketAddButton compId={compId} bucket="text" order={totalElements} />
          )}
        </>
      )}
    </div>
  );

  const FilesBlock = files.length > 0 ? (
    <BucketRow elements={files} compId={compId} className="w-full" />
  ) : null;

  if (isTextOnly) {
    return (
      <div className="w-full flex flex-col items-center group/centered">
        {ContentBlock}
        {ImagesBlock}
        {FilesBlock}
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center group/centered">
      {ImagesBlock}
      {ContentBlock}
      {FilesBlock}
    </div>
  );
};

export default React.memo(ComponentCentered);
